import type { HealthRollup, ScopeFilter } from '@@/api/health';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { filterRollup } from '@@/api/health/health-rollup';
import { useOrganizations } from './useOrganizations';

export type HealthRollupResponse = HealthRollup & {
  scope: ScopeFilter;
  /** Whether the viewer sees a client roster or their own single account. */
  isAgency: boolean;
};

/**
 * No timespan. None of the health signals are windowed, so a date control would
 * imply the page re-derives per period when it does not.
 *
 * Search runs on the client because the whole rollup is already here and it is
 * small: an agency's worth of items is a few hundred rows, and filtering them
 * locally is instant instead of a round trip per keystroke.
 */
export const useHealthRollup = () => {
  const client = useClient();
  const { organization } = useOrganizations();
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [search, setSearch] = useState('');

  // Sent only for the account scope, and validated server-side against what the
  // caller can actually reach.
  const accountId = organization?.id ?? 0;

  const { data, isLoading, isError, refetch } = useQuery<HealthRollupResponse>({
    queryKey: ['HEALTH_ROLLUP', scope, scope === 'account' ? accountId : null],
    // No account id in the path: the server resolves reach from the session.
    queryFn: () => client.get<HealthRollupResponse>(`/health/rollup?scope=${scope}&accountId=${accountId}`),
  });

  const rollup = data && search ? { ...data, ...filterRollup(data, search) } : data;

  // isError, not just `!rollup`: a failed request leaves isLoading false and the data
  // undefined, which is indistinguishable from the first render unless the flag comes
  // through — the screen otherwise says it is loading forever.
  return { rollup, isLoading, isError, refetch, scope, setScope, search, setSearch };
};

export type UseHealthRollupType = ReturnType<typeof useHealthRollup>;
